import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"

import { FAQ } from "@/types/chatbot"
import { fetchFAQs, saveFAQ } from "@/lib/api/assistant"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"

import { Icons } from "../icons"

interface FAQsProps {
  onQuestionClick: (faq: FAQ) => void
}

export const FAQs: React.FC<FAQsProps> = ({ onQuestionClick }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const {
    data: faqs,
    isLoading,
    error,
  } = useQuery<FAQ[]>({
    queryKey: ["faqs"],
    queryFn: fetchFAQs,
  })

  if (isLoading) return null
  if (error) return null

  const handleQuestionClick = async (faq: FAQ) => {
    await saveFAQ(faq.id)
    onQuestionClick(faq)
    setIsExpanded(false)
  }

  const toggleExpand = () => setIsExpanded(!isExpanded)

  return (
    <div className="w-full border-b border-t">
      <div className="p-2">
        <Button
          variant="ghost"
          className="w-full justify-between"
          onClick={toggleExpand}
        >
          <span>Frequently Asked Questions</span>
          {isExpanded ? (
            <Icons.up className="ml-2 h-4 w-4" />
          ) : (
            <Icons.down className="ml-2 h-4 w-4" />
          )}
        </Button>
        {isExpanded && (
          <ScrollArea className="mt-2 h-[200px]">
            <div className="space-y-2 px-2">
              {faqs?.map((faq, index) => (
                <div key={index} className="flex flex-col">
                  <Button
                    variant="link"
                    className="h-auto justify-start p-0 text-left"
                    onClick={() => handleQuestionClick(faq)}
                  >
                    {faq.question}
                  </Button>
                  {faq.link && (
                    <a
                      href={faq.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline"
                    >
                      Learn more
                    </a>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  )
}
